import { defineComponent as _defineComponent } from 'vue'
import { ref as _ref, computed as _computed } from 'vue';

import { watch } from 'vue';
import { Vue } from 'vue-property-decorator';
import type { DataOptions } from 'vuetify';
import {
  DataProductService,
  type GetDataProductResponseDto,
  type AssetSummaryDtoV2,
  type IncidentCatalogAssetDto,
  type DataProductUpstreamIncidentDto,
  IncidentService,
  UserUiDto,
} from '@/api';
import { formatSchedule } from '@/utils/schedule';
import { type GenericEntity } from '@/components/generic-entity/@types/GenericEntityTypes';
import { getModule } from 'vuex-module-decorators';
import authModule from '@/store/modules/auth';
import store from '@/store';
import i18n from '@/i18n';
import SButton from '@/components/SButton/SButton.vue';
import SLabel from '@/components/SLabel.vue';
import SIcon from '@/components/SIcon.vue';
import Tags from '@/components/tags/Tags.vue';
import SAvatars from '@/components/SAvatars/SAvatars.vue';
import SRichTextViewer from '@/components/SRichTextViewer.vue';
import DataProductsDrawerDataAssetsTab from '@/modules/data-products/right-drawer/DataProductsDrawerDataAssetsTab.vue';
import DataProductsDrawerTitle from '@/modules/data-products/right-drawer/DataProductsDrawerTitle.vue';
import SNavigationDrawer from '@/components/SNavigationDrawer/SNavigationDrawer.vue';
import SiffletifyDataFooter from '@/components/siffletify/siffletify-data-footer/siffletify-data-footer.vue';
import IncidentsResultsCard from '@/components/incidents/Incidents-Results-Card.vue';
import { composeSortValues } from '@/utils/sort';

type AssetFilterType = 'TABLE_AND_VIEW' | 'DASHBOARD' | 'PIPELINE' | 'GENERIC';
type IncidentsScope = 'DATA_PRODUCT' | 'UPSTREAM';

interface DataProductsDrawerProps {
  item: GenericEntity | null;
  isOpen: boolean;
}

interface DataProductsDrawerEmits {
  (event: 'delete-item', id: GenericEntity): void;
  (event: 'update-item', item: GenericEntity): void;
  (event: 'on-cancel'): void;
}

export default /*#__PURE__*/_defineComponent({
  __name: 'DataProductsDrawer',
  props: {
  item: { default: null },
  isOpen: { type: Boolean }
},
  emits: ["delete-item", "update-item", "on-cancel"],
  setup(__props, { emit: emits }) {

const props = __props;




const DEFAULT_OPTIONS = {
  page: 1,
  itemsPerPage: 25,
};

const DEFAULT_INCIDENTS_OPTIONS = {
  page: 1,
  itemsPerPage: 10,
  sort: ['createdDate,DESC'] as string[],
};

const auth = getModule(authModule, store);
const activeTab = _ref(0);

let dataProduct = _ref<GetDataProductResponseDto | null>(null);
let assetFilterType = _ref<AssetFilterType | undefined>(undefined);
const options = _ref(DEFAULT_OPTIONS);
let assets = _ref<AssetSummaryDtoV2[]>([]);
let assetsCount = _ref<number>(0);

let incidentsScope = _ref<IncidentsScope>('DATA_PRODUCT');
let drawerIncidents = _ref<IncidentCatalogAssetDto[] | DataProductUpstreamIncidentDto[]>([]);
let drawerIncidentsCount = _ref(0);
let drawerIncidentsLoading = _ref(false);
let incidentsIteratorOptions = _ref({
  ...DEFAULT_INCIDENTS_OPTIONS,
  sortBy: ['createdDate'] as string[],
  sortDesc: [true] as boolean[],
});

const canEdit = _computed(() => auth.userActions['data_product.write']);
const canDelete = _computed(() => auth.userActions['data_product.write']);

const name = _computed(() => dataProduct.value?.name || '-');
const description = _computed(() => dataProduct.value?.description || '\\-');
const domainName = _computed(() => dataProduct.value?.domain?.name || '-');
const domainId = _computed(() => dataProduct.value?.domain?.id || '');
const owners = _computed(() => dataProduct.value?.owners || []);
const tags = _computed(() => dataProduct.value?.tags || []);
const sla = _computed(() => (dataProduct.value?.sla ? formatSchedule(dataProduct.value.sla) : '-'));
const isSystemAdmin = _computed(() => auth.getUserRole === UserUiDto.role.ADMIN);

const editDataProduct = () => {
  if (canEdit.value && props.item?.id) {
    emits('update-item', props.item);
  }
};

const deleteDataProduct = () => {
  if (canDelete.value && props.item) {
    emits('delete-item', props.item);
  }
};

const getAssetsLinkedToDataProduct = async () => {
  if (!props.item) return;

  const { id } = props.item;

  const response = await DataProductService.getDataProductAssets({
    dataProductId: id!,
    assetFilterType: assetFilterType.value ? [assetFilterType.value] : undefined,
    page: options.value.page,
    itemsPerPage: options.value.itemsPerPage,
    sort: ['name,ASC'],
  });
  assets.value = response.data || [];
  assetsCount.value = response.filteredElements || 0;
};

const clearDrawerIncidents = () => {
  drawerIncidents.value = [];
  drawerIncidentsCount.value = 0;
};

const fetchOwnedDataProductIncidents = async (product: GetDataProductResponseDto) => {
  const incidentsDomainId = product.domain?.id ?? auth.selectedDomain?.id;
  if (!incidentsDomainId) {
    clearDrawerIncidents();
    return;
  }

  const sort = composeSortValues({
    sortBy: incidentsIteratorOptions.value.sortBy,
    sortDesc: incidentsIteratorOptions.value.sortDesc,
  });
  const { searchIncidents } = await IncidentService.getAllIncidentsV2({
    requestBody: {
      domain: incidentsDomainId,
      status: ['OPEN', 'IN_PROGRESS'],
      page: incidentsIteratorOptions.value.page,
      itemsPerPage: incidentsIteratorOptions.value.itemsPerPage,
      sort: sort.length ? sort : DEFAULT_INCIDENTS_OPTIONS.sort,
      dataProduct: [product.id],
    },
  });
  drawerIncidents.value = searchIncidents.data ?? [];
  drawerIncidentsCount.value = searchIncidents.totalElements ?? 0;
};

const fetchDataProductUpstreamIncidents = async (dataProductId: string) => {
  const { data, totalElements } = await DataProductService.getDataProductUpstreamIncidents({
    id: dataProductId,
    page: incidentsIteratorOptions.value.page,
    itemsPerPage: incidentsIteratorOptions.value.itemsPerPage,
  });
  drawerIncidents.value = data ?? [];
  drawerIncidentsCount.value = totalElements ?? 0;
};

const fetchDataProductIncidents = async () => {
  const product = dataProduct.value;
  const isUpstream = incidentsScope.value === 'UPSTREAM';
  if (!product?.id) {
    clearDrawerIncidents();
    return;
  }

  drawerIncidentsLoading.value = true;
  try {
    if (isUpstream) {
      await fetchDataProductUpstreamIncidents(product.id);
    } else {
      await fetchOwnedDataProductIncidents(product);
    }
  } catch {
    clearDrawerIncidents();
    Vue.notify({
      type: 'error',
      text: i18n.t('data_products.right_drawer.incidents_load_error') as string,
    });
  } finally {
    drawerIncidentsLoading.value = false;
  }
};

const updateIncidentsIteratorOptions = (opts: Partial<DataOptions>) => {
  const { itemsPerPage, sortBy, sortDesc } = opts;
  const sort = composeSortValues({ sortBy, sortDesc });
  const page = opts.itemsPerPage !== incidentsIteratorOptions.value.itemsPerPage
    ? 1
    : (opts.page ?? incidentsIteratorOptions.value.page);
  incidentsIteratorOptions.value = {
    ...incidentsIteratorOptions.value,
    page,
    itemsPerPage: itemsPerPage ?? incidentsIteratorOptions.value.itemsPerPage,
    sortBy: sortBy ?? incidentsIteratorOptions.value.sortBy,
    sortDesc: sortDesc ?? incidentsIteratorOptions.value.sortDesc,
    sort: sort.length ? sort : DEFAULT_INCIDENTS_OPTIONS.sort,
  };
};

const handleUpdateFilter = (newAssetFilterType: AssetFilterType | undefined) => {
  assetFilterType.value = newAssetFilterType;
  getAssetsLinkedToDataProduct();
};

const handleCloseDrawer = () => emits('on-cancel');

const initializeDrawer = async () => {
  if (!props.item) return;
  const { id } = props.item;
  dataProduct.value = await DataProductService.getDataProduct({ id: id! });
};

const routeToDomain = (id: string) => ({
  name: 'domains.domain.edit',
  params: { id },
});

watch(() => incidentsScope.value, () => {
  incidentsIteratorOptions.value = { ...incidentsIteratorOptions.value, page: 1 };
});

watch(() => options.value, () => {
  getAssetsLinkedToDataProduct();
});

watch(
  () => props.item,
  async (newItem: GenericEntity | null, oldItem: GenericEntity | null | undefined) => {
    if (!newItem) return;
    const idChanged = newItem.id !== oldItem?.id;
    if (idChanged) {
      options.value.page = DEFAULT_OPTIONS.page;
      options.value.itemsPerPage = DEFAULT_OPTIONS.itemsPerPage;
      assetFilterType.value = undefined;
      incidentsIteratorOptions.value = {
        ...DEFAULT_INCIDENTS_OPTIONS,
        sortBy: ['createdDate'],
        sortDesc: [true],
      };
      incidentsScope.value = 'DATA_PRODUCT';
    }
    await initializeDrawer();
    await getAssetsLinkedToDataProduct();
  },
  { immediate: true },
);

watch(
  () => ({
    itemId: props.item?.id,
    dataProductId: dataProduct.value?.id,
    scope: incidentsScope.value,
    page: incidentsIteratorOptions.value.page,
    itemsPerPage: incidentsIteratorOptions.value.itemsPerPage,
    sortKey: `${(incidentsIteratorOptions.value.sortBy ?? []).join(',')}:${(incidentsIteratorOptions.value.sortDesc ?? []).join(',')}`,
  }),
  async () => {
    if (!props.item?.id || dataProduct.value?.id !== props.item.id) return;
    await fetchDataProductIncidents();
  },
  { immediate: true },
);

return { __sfc: true,props, emits, DEFAULT_OPTIONS, DEFAULT_INCIDENTS_OPTIONS, auth, activeTab, dataProduct, assetFilterType, options, assets, assetsCount, incidentsScope, drawerIncidents, drawerIncidentsCount, drawerIncidentsLoading, incidentsIteratorOptions, canEdit, canDelete, name, description, domainName, domainId, owners, tags, sla, isSystemAdmin, editDataProduct, deleteDataProduct, getAssetsLinkedToDataProduct, clearDrawerIncidents, fetchOwnedDataProductIncidents, fetchDataProductUpstreamIncidents, fetchDataProductIncidents, updateIncidentsIteratorOptions, handleUpdateFilter, handleCloseDrawer, initializeDrawer, routeToDomain, _ref, _computed, watch, Vue, DataProductService, IncidentService, UserUiDto, formatSchedule, getModule, authModule, store, i18n, SButton, SLabel, SIcon, Tags, SAvatars, SRichTextViewer, DataProductsDrawerDataAssetsTab, DataProductsDrawerTitle, SNavigationDrawer, SiffletifyDataFooter, IncidentsResultsCard, composeSortValues }
}

})